import React, { useState, useRef, useEffect } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/solid';

const DropdownCustom = ({ 
  options = [],
  selectedValue = '',
  placeholder = 'Select',
  width,
  onSelect = () => {},
  onToggle = () => {},
  buttonClassName = '',
  dropdownClassName = '',
  optionClassName = '',
  disabled = false,
  showAllOption = false,
  allOptionText = 'All',
  allOptionValue = '',
  position = 'right',
  zIndex = 'z-10'
}) => {
  // ##### STATES
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // ##### EFFECTS
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ##### HANDLERS
  const handleToggle = () => {
    if (disabled) return;
    
    const newState = !isOpen;
    setIsOpen(newState);
    onToggle(newState);
  };

  const handleSelect = (value) => {
    onSelect(value);
    setIsOpen(false);
  };

  // ##### POSITIONING
  const getPositionClasses = () => {
    switch (position) {
      case 'left':
        return 'left-0';
      case 'center':
        return 'left-1/2 transform -translate-x-1/2';
      case 'right':
      default:
        return 'right-0';
    }
  };

  // ##### RENDER
  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={handleToggle}
        disabled={disabled}
        className={`
          inline-flex justify-between items-center ${width || 'w-full'} rounded-md border 
          bg-white border-gray-300 px-2 py-1.5 text-xs text-gray-700 
          hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-1 
          focus:ring-blue-500 focus:border-blue-500 transition-all
          ${disabled ? 'opacity-50 cursor-not-allowed bg-gray-100' : 'cursor-pointer'}
          ${buttonClassName}
        `}
      >
        <span className="truncate">
          {selectedValue || placeholder}
        </span>
        <ChevronDownIcon 
          className={`ml-1 h-4 w-4 text-gray-500 transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`} 
        />
      </button>

      {isOpen && (
        <div className={`
          absolute top-full ${getPositionClasses()} ${zIndex} mt-1 
          ${width || 'w-full'} rounded-md bg-white shadow-lg border border-gray-200 
          max-h-60 overflow-y-auto
          ${dropdownClassName}
        `}>
          {showAllOption && (
            <div
              onClick={() => handleSelect(allOptionValue)}
              className={`
                cursor-pointer px-3 py-1.5 text-xs hover:bg-gray-50 
                ${selectedValue === allOptionValue ? 'text-gray-900 bg-gray-100' : 'text-gray-700'}
                ${optionClassName}
              `}
            >
              {allOptionText}
            </div>
          )}

          {options.map((option, index) => {
            const optionValue = typeof option === 'object' ? option.value : option;
            const optionLabel = typeof option === 'object' ? option.label : option;
            const isSelected = selectedValue === optionValue;
            
            return (
              <div
                key={index}
                onClick={() => handleSelect(optionValue)}
                className={`
                  cursor-pointer px-3 py-1.5 text-xs hover:bg-gray-50 
                  ${isSelected ? 'text-gray-900 bg-gray-100' : 'text-gray-700'}
                  ${optionClassName}
                `}
              >
                {optionLabel}
              </div>
            );
          })}

          {options.length === 0 && (
            <div className="px-3 py-1.5 text-xs text-gray-500 italic">
              No options available
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DropdownCustom;
